import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useModal } from '../contexts/ModalContext';
import { X, AlertCircle, CheckCircle2, HelpCircle, Trash2, AlertTriangle } from 'lucide-react';

const ModalOverlay = ({ children, onClose }) => {
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                e.stopImmediatePropagation();
                onClose();
            }
        };
        window.addEventListener('keydown', handleEsc, true);
        return () => window.removeEventListener('keydown', handleEsc, true);
    }, [onClose]);

    return createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center" style={{ zIndex: 99999 }} onClick={onClose}>
            <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-sm mx-6 overflow-hidden border border-white/20" onClick={e => e.stopPropagation()}>
                {children}
            </div>
        </div>,
        document.body
    );
};

export const GlobalModals = () => {
    const { modal, closeModal } = useModal();
    const { type, props } = modal;
    const [inputValue, setInputValue] = useState("");
    const inputRef = useRef(null);

    // [3D Icons]
    const ICONS = {
        delete: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/Wastebasket.png",
        warning: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Symbols/Warning.png",
        check: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Symbols/Check%20Mark%20Button.png",
        question: "/images/Red_question_mark.png",
        backup: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/Card%20Index%20Dividers.png",
        restore: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/File%20Folder.png", // Swapped for variety or keep consistent
        info: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/Light%20Bulb.png"
    };

    useEffect(() => {
        if (type === 'prompt') {
            setInputValue(props?.defaultValue || "");
            // Focus input after render
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [type, props?.defaultValue]);

    if (!type) return null;

    const handleClose = () => {
        if (props.onCancel) props.onCancel();
        closeModal();
    };

    const handleConfirm = () => {
        if (props.onConfirm) props.onConfirm();
        closeModal();
    };

    const handlePromptSubmit = (e) => {
        e.preventDefault();
        if (props.onSubmit) props.onSubmit(inputValue);
        closeModal();
    };

    // [Smart Icon Logic]
    const renderIcon = () => {
        const title = props?.title || "";
        const message = props?.message || "";

        let iconSrc = ICONS.info; // default

        if (type === 'alert') {
            if (title.includes("성공") || title.includes("완료")) iconSrc = ICONS.check;
            else if (title.includes("오류") || title.includes("실패") || title.includes("주의")) iconSrc = ICONS.warning;
        } else if (type === 'confirm') {
            if (title.includes("삭제") || message.includes("삭제")) iconSrc = ICONS.delete;
            else if (title.includes("백업")) iconSrc = ICONS.backup; // Backup specific
            else if (title.includes("복구")) iconSrc = ICONS.restore; // Restore specific
            else iconSrc = ICONS.question;
        }

        return (
            <img
                src={iconSrc}
                alt="icon"
                className="w-16 h-16 object-contain drop-shadow-lg"
                onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://cdn-icons-png.flaticon.com/512/11522/11522822.png"; // Fallback
                }}
            />
        );
    };

    const getConfirmButtonColor = () => {
        if (props?.confirmButtonClass) return props.confirmButtonClass; // [NEW] Custom Override
        const title = props?.title || "";
        if (type === 'confirm' && (title.includes("삭제") || (props?.message || "").includes("삭제"))) return "bg-red-50 text-red-500 hover:bg-red-100";
        return "bg-[#354F38] text-white hover:bg-[#2C3E30] shadow-lg shadow-[#354F38]/30";
    }

    return (
        <ModalOverlay onClose={handleClose}>
            {/* Premium Glass Styling */}
            <div className="bg-white/90 backdrop-blur-2xl p-6 text-center rounded-[2.5rem] relative z-10">
                <div className="flex justify-center mb-5">
                    <div className="p-4 bg-white/50 rounded-3xl shadow-sm border border-white/60">
                        {renderIcon()}
                    </div>
                </div>

                <h3 className="text-2xl font-black text-[#2C3E30] mb-3 font-do-hyeon drop-shadow-sm">{props.title}</h3>
                <p className="text-gray-500 text-sm font-medium leading-relaxed break-keep whitespace-pre-wrap mb-6">{props.message}</p>

                {type === 'prompt' && (
                    <form onSubmit={handlePromptSubmit} className="mt-4 mb-6">
                        <input
                            ref={inputRef}
                            type="text"
                            className="w-full border border-gray-200 rounded-2xl p-4 text-center font-bold text-gray-700 focus:ring-4 focus:ring-[#354F38]/10 focus:border-[#354F38] outline-none transition bg-gray-50/50 focus:bg-white"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                        />
                    </form>
                )}

                <div className="flex gap-3">
                    {type === 'alert' ? (
                        <button onClick={() => { if (props.onOk) props.onOk(); closeModal(); }} className="w-full py-3.5 bg-[#354F38] text-white font-bold rounded-xl hover:bg-[#2C3E30] transition shadow-lg shadow-[#354F38]/20 active:scale-95">
                            확인
                        </button>
                    ) : (
                        <>
                            <button onClick={handleClose} className="flex-1 py-3.5 text-gray-400 font-bold bg-gray-100 rounded-xl hover:bg-gray-200 transition active:scale-95">
                                취소
                            </button>
                            {type === 'prompt' ? (
                                <button onClick={handlePromptSubmit} className="flex-1 py-3.5 bg-[#354F38] text-white font-bold rounded-xl hover:bg-[#2C3E30] transition shadow-lg shadow-[#354F38]/20 active:scale-95">
                                    확인
                                </button>
                            ) : (
                                <button onClick={handleConfirm} className={`flex-1 py-3.5 font-bold rounded-xl transition active:scale-95 ${getConfirmButtonColor()}`}>
                                    확인
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </ModalOverlay>
    );
};